<script setup lang="ts">
import VuePdfEmbed from 'vue-pdf-embed'

import type { WaitingRoomDuplicateComparisonFile } from '@/api/fileManagement'
import { isImageFile, isPdfFile } from '@/helpers/utils/files/filePreview'

interface Props {
  file: WaitingRoomDuplicateComparisonFile
  selected: boolean
  scale: number
  page: number
  pageCount: number
  loading: boolean
  previewActive: boolean
  previewFailed?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  previewFailed: false,
})

interface Emit {
  (event: 'select', fileId: string): void
  (event: 'page', fileId: string, direction: -1 | 1): void
  (event: 'loaded', fileId: string, pageCount: number): void
  (event: 'rendered', fileId: string, pageCount: number): void
  (event: 'pdf-error', fileId: string): void
}

const emit = defineEmits<Emit>()

const PDF_RENDER_WIDTH = 1600
const DEFAULT_PDF_ASPECT_RATIO = 1 / Math.SQRT2

const pdfRef = ref<any>(null)
const previewRef = ref<HTMLElement | null>(null)
const viewportWidth = ref(0)
const pageAspectRatio = ref(DEFAULT_PDF_ASPECT_RATIO)
let previewResizeObserver: ResizeObserver | null = null
let previewResizeFrame: number | null = null
let aspectRatioRequestId = 0
const checkboxProps = {
  label: 'Waiting Room File',
  color: 'primary',
  hideDetails: true,
  density: 'compact' as const,
}
const dragState = ref<{
  target: HTMLElement | null
  left: number
  top: number
  x: number
  y: number
}>({
  target: null,
  left: 0,
  top: 0,
  x: 0,
  y: 0,
})

const fileUrl = computed(() => props.file.preview_url || props.file.url || props.file.download_url || null)
const createdByDisplay = computed(() => props.file.file?.created_by_display || '-')
const pdfEmbedKey = computed(() => `${props.file.id}-${fileUrl.value || 'no-url'}`)
const isPdfPreview = computed(() => isPdfFile(props.file.filename) && Boolean(fileUrl.value))
const isImagePreview = computed(() => isImageFile(props.file.filename) && Boolean(fileUrl.value))
const zoomFactor = computed(() => Math.min(5, Math.max(1, Number(props.scale) / 100 || 1)))
const fitScale = computed(() => {
  if (!viewportWidth.value) return 1

  return Math.min(1, viewportWidth.value / PDF_RENDER_WIDTH)
})
const pdfDisplayScale = computed(() => fitScale.value * zoomFactor.value)
const pdfDisplayHeight = computed(() => PDF_RENDER_WIDTH / pageAspectRatio.value)
const pdfStageStyle = computed(() => ({
  width: `${PDF_RENDER_WIDTH * pdfDisplayScale.value}px`,
  height: `${pdfDisplayHeight.value * pdfDisplayScale.value}px`,
}))
const pdfEmbedStyle = computed(() => ({
  width: `${PDF_RENDER_WIDTH}px`,
  transform: `scale(${pdfDisplayScale.value})`,
}))
const mediaZoomStyle = computed(() => ({
  width: `${zoomFactor.value * 100}%`,
  minHeight: `${420 * zoomFactor.value}px`,
}))

const updateViewportWidth = () => {
  const width = previewRef.value?.getBoundingClientRect().width || 0
  if (width > 0) {
    viewportWidth.value = Math.floor(width)
  }
}

const scheduleViewportWidthUpdate = () => {
  if (previewResizeFrame !== null) {
    window.cancelAnimationFrame(previewResizeFrame)
  }

  previewResizeFrame = window.requestAnimationFrame(() => {
    previewResizeFrame = null
    updateViewportWidth()
  })
}

const pdfPageCount = (loadedDocument?: { numPages?: number }) => {
  return loadedDocument?.numPages || pdfRef.value?.pageCount || 1
}

const updatePageAspectRatio = async (document?: { getPage?: (page: number) => Promise<any> }) => {
  const sourceDocument = document || pdfRef.value?.document
  if (!sourceDocument?.getPage || !isPdfPreview.value) return

  const requestId = ++aspectRatioRequestId

  try {
    const page = await sourceDocument.getPage(props.page)
    const viewport = page?.getViewport?.({ scale: 1 })
    if (
      requestId !== aspectRatioRequestId
      || !viewport?.width
      || !viewport?.height
    ) return

    pageAspectRatio.value = viewport.width / viewport.height
  } catch {
    // The PDF renderer reports load/render failures through its dedicated events.
  }
}

const onPdfLoaded = (loadedDocument?: { numPages?: number; getPage?: (page: number) => Promise<any> }) => {
  void updatePageAspectRatio(loadedDocument)
  emit('loaded', props.file.id, pdfPageCount(loadedDocument))
}

const onPdfRendered = async () => {
  await nextTick()
  void updatePageAspectRatio()
  emit('rendered', props.file.id, pdfPageCount())
}

const startPreviewPan = (event: MouseEvent) => {
  if (event.button !== 0) return
  if ((event.target as HTMLElement).closest('button, input, textarea, select, a')) return

  const target = event.currentTarget as HTMLElement
  dragState.value = {
    target,
    left: target.scrollLeft,
    top: target.scrollTop,
    x: event.clientX,
    y: event.clientY,
  }
}

const movePreviewPan = (event: MouseEvent) => {
  const state = dragState.value
  if (!state.target) return

  event.preventDefault()
  state.target.scrollLeft = state.left - (event.clientX - state.x)
  state.target.scrollTop = state.top - (event.clientY - state.y)
}

const stopPreviewPan = () => {
  dragState.value.target = null
}

watch(
  () => props.page,
  () => {
    void updatePageAspectRatio()
  },
)

onMounted(() => {
  updateViewportWidth()

  if (previewRef.value && typeof ResizeObserver !== 'undefined') {
    previewResizeObserver = new ResizeObserver(scheduleViewportWidthUpdate)
    previewResizeObserver.observe(previewRef.value)
  }
})

onBeforeUnmount(() => {
  previewResizeObserver?.disconnect()
  previewResizeObserver = null

  if (previewResizeFrame !== null) {
    window.cancelAnimationFrame(previewResizeFrame)
    previewResizeFrame = null
  }
})
</script>

<template>
  <article
    class="waiting-room-duplicate-comparison-file-tile"
    :class="{ 'waiting-room-duplicate-comparison-file-tile--selected': selected }"
  >
    <div class="waiting-room-duplicate-comparison-file-header">
      <VCheckbox
        :model-value="selected"
        v-bind="checkboxProps"
        @update:model-value="emit('select', file.id)"
      />
      <div class="waiting-room-duplicate-comparison-file-meta">
        <div class="waiting-room-duplicate-comparison-file-created-by text-caption text-medium-emphasis">
          <span class="text-truncate">
            Created By: {{ createdByDisplay }}
          </span>
          <VMenu
            activator="parent"
            location="top center"
            open-on-hover
            open-delay="100"
            close-delay="0"
            offset="1"
            class="default-tooltip"
          >
            <span class="px-2">
              Created By: {{ createdByDisplay }}
            </span>
          </VMenu>
        </div>
      </div>
    </div>

    <div
      ref="previewRef"
      class="waiting-room-duplicate-comparison-preview waiting-room-duplicate-comparison-tile-preview"
      @mousedown="startPreviewPan"
      @mousemove="movePreviewPan"
      @mouseup="stopPreviewPan"
      @mouseleave="stopPreviewPan"
    >
      <div
        v-if="loading"
        class="waiting-room-duplicate-comparison-preview-loader"
      >
        <VProgressCircular
          indeterminate
          color="primary"
        />
      </div>

      <img
        v-if="isImagePreview"
        :src="fileUrl || undefined"
        :alt="file.filename"
        loading="lazy"
        class="waiting-room-duplicate-comparison-image"
        :style="mediaZoomStyle"
      >

      <div
        v-else-if="isPdfPreview && previewFailed"
        class="waiting-room-duplicate-comparison-empty"
      >
        Preview is not available.
      </div>

      <div
        v-else-if="isPdfPreview && previewActive"
        class="waiting-room-duplicate-comparison-pdf-container"
      >
        <div class="waiting-room-duplicate-comparison-pages">
          <VBtn
            icon="mdi-arrow-left-bold-circle-outline"
            size="small"
            variant="text"
            :disabled="page <= 1"
            @click="emit('page', file.id, -1)"
          />
          <span>{{ page }} / {{ pageCount }}</span>
          <VBtn
            icon="mdi-arrow-right-bold-circle-outline"
            size="small"
            variant="text"
            :disabled="page >= pageCount"
            @click="emit('page', file.id, 1)"
          />
        </div>
        <div
          class="waiting-room-duplicate-comparison-pdf-stage"
          :style="pdfStageStyle"
        >
          <VuePdfEmbed
            :key="pdfEmbedKey"
            ref="pdfRef"
            :source="fileUrl"
            :page="page"
            :width="PDF_RENDER_WIDTH"
            :style="pdfEmbedStyle"
            class="waiting-room-duplicate-comparison-pdf"
            @loaded="onPdfLoaded"
            @rendered="onPdfRendered"
            @loading-failed="emit('pdf-error', file.id)"
            @rendering-failed="emit('pdf-error', file.id)"
          />
        </div>
      </div>

      <div
        v-else-if="isPdfPreview"
        class="waiting-room-duplicate-comparison-empty"
      >
        Loading preview…
      </div>

      <iframe
        v-else-if="fileUrl"
        :src="fileUrl || undefined"
        :title="file.filename"
        :style="mediaZoomStyle"
        class="waiting-room-duplicate-comparison-iframe"
      />

      <div
        v-else
        class="waiting-room-duplicate-comparison-empty"
      >
        Preview is not available.
      </div>
    </div>
  </article>
</template>

<style scoped lang="scss">
.waiting-room-duplicate-comparison-file-tile {
  background: rgb(var(--v-theme-surface));
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  min-block-size: 360px;
  min-inline-size: 0;
  overflow: hidden;
  transition: background-color 0.15s ease, border-color 0.15s ease;
}

.waiting-room-duplicate-comparison-file-tile--selected {
  background: rgba(var(--v-theme-primary), 0.1);
  border-color: rgba(var(--v-theme-primary), 0.55);
}

.waiting-room-duplicate-comparison-file-header {
  align-items: center;
  border-block-end: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  display: grid;
  flex: 0 0 auto;
  gap: 8px;
  grid-template-columns: auto minmax(0, 1fr);
  padding: 8px 10px;
}

.waiting-room-duplicate-comparison-file-meta {
  display: flex;
  justify-content: flex-end;
  min-inline-size: 0;
}

.waiting-room-duplicate-comparison-file-created-by {
  display: flex;
  justify-content: flex-end;
  max-inline-size: 100%;
  min-inline-size: 0;
  text-align: end;
}

.waiting-room-duplicate-comparison-preview {
  cursor: grab;
  flex: 1 1 auto;
  min-block-size: 0;
  overflow: auto;
  padding: 10px;
  position: relative;
}

.waiting-room-duplicate-comparison-preview-loader,
.waiting-room-duplicate-comparison-empty {
  align-items: center;
  display: flex;
  block-size: 100%;
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
  justify-content: center;
}

.waiting-room-duplicate-comparison-preview-loader {
  inset: 0;
  pointer-events: none;
  position: absolute;
  z-index: 2;
}

.waiting-room-duplicate-comparison-image {
  block-size: auto;
  display: block;
  margin: 0 auto;
  max-inline-size: none;
  object-fit: contain;
}

.waiting-room-duplicate-comparison-pdf-container {
  min-block-size: 100%;
}

.waiting-room-duplicate-comparison-pages {
  align-items: center;
  display: flex;
  gap: 8px;
  justify-content: center;
  min-block-size: 36px;
}

.waiting-room-duplicate-comparison-pdf-stage {
  margin: 0 auto;
  position: relative;
}

.waiting-room-duplicate-comparison-pdf {
  display: block;
  transform-origin: top left;
  will-change: transform;
}

.waiting-room-duplicate-comparison-iframe {
  border: 0;
  display: block;
  margin: 0 auto;
}
</style>
